import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The device's wall-clock time in the shape `<input type="datetime-local">`
 * expects: `YYYY-MM-DDTHH:mm:ss`, with no timezone suffix.
 *
 * Built from the local getters rather than the `new Date(d - offset)
 * .toISOString()` trick. Both produce the same digits — I checked, including
 * across DST boundaries in several zones — but this one says what it means
 * instead of fabricating a fake UTC instant to read local digits off.
 */
export const toLocalInputValue = (date = new Date()) => {
    const pad = (n) => String(n).padStart(2, "0");
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
};

/**
 * Turns what the input holds into an absolute instant to send to the server.
 *
 * This is the part that keeps the DEVICE's clock authoritative. A bare
 * `YYYY-MM-DDTHH:mm:ss` has no offset, so the server would parse it in the
 * SERVER's timezone — a shop in Accra posting to a server in another zone
 * would have every sale silently recorded hours out. Parsing it here (where
 * the browser applies the device's own offset) and sending a full ISO instant
 * removes the server's clock and timezone from the question entirely.
 */
export const toInstant = (localValue) => {
    if (!localValue) return new Date().toISOString();
    const parsed = new Date(localValue);
    return Number.isNaN(parsed.getTime())
        ? new Date().toISOString()
        : parsed.toISOString();
};

/**
 * A sale-date field that keeps up with the clock.
 *
 * A till can sit open on this page for hours; capturing the time once when the
 * page mounted meant an 11:30 sale was recorded at 10:20. So it ticks every
 * second — until the user types their own date, at which point it stops dead.
 * Backdating a sale on purpose is a legitimate thing to do, and a clock that
 * overwrites the number you just typed is worse than a stale one.
 *
 * Returns `[value, setValue, { isLive, resetToNow }]`.
 */
export const useLiveSaleDate = () => {
    const [value, setValue] = useState(() => toLocalInputValue());
    const [isLive, setIsLive] = useState(true);
    // Read inside the interval without making it a dependency, so the timer is
    // installed once instead of being torn down and rebuilt every second.
    const liveRef = useRef(isLive);
    liveRef.current = isLive;

    useEffect(() => {
        const id = setInterval(() => {
            if (liveRef.current) setValue(toLocalInputValue());
        }, 1000);
        return () => clearInterval(id);
    }, []);

    /** The user typed a date: stop following the clock. */
    const set = useCallback((next) => {
        setIsLive(false);
        setValue(next);
    }, []);

    /** Re-arm: snap back to now and start following again. */
    const resetToNow = useCallback(() => {
        setIsLive(true);
        setValue(toLocalInputValue());
    }, []);

    /**
     * Adopt a date from elsewhere (editing an existing sale, loading a draft)
     * without treating it as a user edit — it should not tick, and it is not
     * something the user typed.
     */
    const adopt = useCallback((date) => {
        setIsLive(false);
        setValue(toLocalInputValue(new Date(date)));
    }, []);

    return [value, set, { isLive, resetToNow, adopt }];
};
